import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { IndianRupee, TrendingUp, TrendingDown, Users, Plus, Edit, Trash2, FileDown } from 'lucide-react';
import RecordPaymentModal from './RecordPaymentModal';
import RecordExpenseModal from './RecordExpenseModal';
import EditPaymentModal from './EditPaymentModal';
import EditExpenseModal from './EditExpenseModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { toast } from '../../hooks/use-toast';
import { generatePaymentReceipt } from '../../utils/pdfGenerator';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MoneyManagement = () => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [showEditExpenseModal, setShowEditExpenseModal] = useState(false);
  const [showDeletePaymentModal, setShowDeletePaymentModal] = useState(false);
  const [showDeleteExpenseModal, setShowDeleteExpenseModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Per-call .catch so one bad endpoint doesn't blank the whole page
      const [summaryRes, paymentsRes, expensesRes] = await Promise.all([
        axios.get(`${API}/money/collections/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/money/payments`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/money/expenses`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);

      setSummary(summaryRes.data);
      setPayments((paymentsRes.data || []).slice(0, 10));
      setExpenses((expensesRes.data || []).slice(0, 10));
    } catch (err) {
      console.error('Failed to fetch money data', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Payment handlers
  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setShowEditPaymentModal(true);
  };

  const handleDeletePayment = (payment) => {
    setSelectedPayment(payment);
    setShowDeletePaymentModal(true);
  };

  const confirmDeletePayment = async () => {
    if (!selectedPayment) return;
    setDeleteLoading(true);

    try {
      await axios.delete(`${API}/money/payments/${selectedPayment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Payment Deleted",
        description: "Payment record has been deleted successfully"
      });

      setShowDeletePaymentModal(false);
      setSelectedPayment(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete payment",
        variant: "destructive"
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Expense handlers
  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setShowEditExpenseModal(true);
  };

  const handleDeleteExpense = (expense) => {
    setSelectedExpense(expense);
    setShowDeleteExpenseModal(true);
  };

  const confirmDeleteExpense = async () => {
    if (!selectedExpense) return;
    setDeleteLoading(true);

    try {
      await axios.delete(`${API}/money/expenses/${selectedExpense.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Expense Deleted",
        description: "Expense record has been deleted successfully"
      });

      setShowDeleteExpenseModal(false);
      setSelectedExpense(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete expense",
        variant: "destructive"
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const generateReceipt = async (payment) => {
    try {
      // Fetch tenant and property details
      const [tenantsRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/tenants`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const tenant = tenantsRes.data.find(t => t.id === payment.tenant_id);
      const property = propertiesRes.data.find(p => p.id === payment.property_id);

      generatePaymentReceipt(payment, tenant, property);
      
      toast({
        title: "Receipt Generated",
        description: "PDF receipt has been downloaded"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate receipt",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Money Management</h1>
          <p className="text-gray-600 mt-1">Track collections, dues, and expenses</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => setShowPaymentModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
          >
            <Plus size={20} />
            Record Payment
          </Button>
          <Button
            onClick={() => setShowExpenseModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
          >
            <Plus size={20} />
            Record Expense
          </Button>
        </div>
      </div>

      {/* Modals */}
      <RecordPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={fetchData}
        token={token}
      />
      <RecordExpenseModal
        isOpen={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        onSuccess={fetchData}
        token={token}
      />

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6 border-l-4 border-l-green-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Collected</p>
              <p className="text-3xl font-bold text-green-700 mt-1">
                ₹{summary?.total_collected?.toLocaleString() || 0}
              </p>
            </div>
            <TrendingUp className="text-green-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-red-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Pending Dues</p>
              <p className="text-3xl font-bold text-red-700 mt-1">
                ₹{summary?.total_pending?.toLocaleString() || 0}
              </p>
            </div>
            <TrendingDown className="text-red-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-blue-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Collection Rate</p>
              <p className="text-3xl font-bold text-blue-700 mt-1">
                {summary?.collection_rate?.toFixed(1) || 0}%
              </p>
            </div>
            <IndianRupee className="text-blue-600" size={32} />
          </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-purple-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Paid Tenants</p>
              <p className="text-3xl font-bold text-purple-700 mt-1">
                {summary?.paid_tenants || 0}/{summary?.total_tenants || 0}
              </p>
            </div>
            <Users className="text-purple-600" size={32} />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Collections */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="text-green-600" size={20} />
            Recent Collections
          </h3>
          {payments.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No collections yet</p>
          ) : (
            <div className="space-y-3">
              {payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-gray-900">₹{payment.amount.toLocaleString()}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        payment.payment_type === 'rent' ? 'bg-blue-100 text-blue-800' :
                        payment.payment_type === 'deposit' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {(() => {
                          const pt = payment.payment_type || 'rent';
                          return pt === 'deposit'
                            ? 'Security Deposit'
                            : pt.charAt(0).toUpperCase() + pt.slice(1);
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-700">
                        👤 {payment.tenant_name || 'Unknown Tenant'}
                      </p>
                      <span className="text-xs text-gray-400">•</span>
                      <p className="text-sm font-medium text-gray-700">
                        🚪 Room {payment.room_number || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-600">{payment.payment_method}</p>
                      <span className="text-xs text-gray-400">•</span>
                      <p className="text-xs text-gray-500">{payment.payment_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-3 py-1 rounded-full ${
                      payment.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {payment.status}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => generateReceipt(payment)}
                      className="h-8 w-8 p-0"
                      title="Download Receipt"
                    >
                      <FileDown className="h-4 w-4 text-green-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditPayment(payment)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePayment(payment)}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Expenses */}
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingDown className="text-red-600" size={20} />
            Recent Expenses
          </h3>
          {expenses.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No expenses recorded</p>
          ) : (
            <div className="space-y-3">
              {expenses.map((expense) => (
                <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">₹{expense.amount}</p>
                    <p className="text-sm text-gray-600">{expense.description}</p>
                    <p className="text-xs text-gray-500">{expense.expense_date}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                      {expense.category}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditExpense(expense)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteExpense(expense)}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Modals */}
      <RecordPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={fetchData}
        token={token}
      />

      <RecordExpenseModal
        isOpen={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        onSuccess={fetchData}
        token={token}
      />

      <EditPaymentModal
        isOpen={showEditPaymentModal}
        onClose={() => {
          setShowEditPaymentModal(false);
          setSelectedPayment(null);
        }}
        onSuccess={fetchData}
        payment={selectedPayment}
        token={token}
      />

      <EditExpenseModal
        isOpen={showEditExpenseModal}
        onClose={() => {
          setShowEditExpenseModal(false);
          setSelectedExpense(null);
        }}
        onSuccess={fetchData}
        expense={selectedExpense}
        token={token}
      />

      <DeleteConfirmationModal
        open={showDeletePaymentModal}
        onClose={() => {
          setShowDeletePaymentModal(false);
          setSelectedPayment(null);
        }}
        onConfirm={confirmDeletePayment}
        title="Delete Payment"
        message="Are you sure you want to delete this payment record?"
        itemName={selectedPayment ? `₹${selectedPayment.amount} - ${selectedPayment.payment_type}` : ''}
        loading={deleteLoading}
      />

      <DeleteConfirmationModal
        open={showDeleteExpenseModal}
        onClose={() => {
          setShowDeleteExpenseModal(false);
          setSelectedExpense(null);
        }}
        onConfirm={confirmDeleteExpense}
        title="Delete Expense"
        message="Are you sure you want to delete this expense record?"
        itemName={selectedExpense ? `₹${selectedExpense.amount} - ${selectedExpense.category}` : ''}
        loading={deleteLoading}
      />
    </div>
  );
};

export default MoneyManagement;
